import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './JamaStoryOutline.css';

/* ═══════════════════════════════════════════════════════════════
   KURZGESAGT human characters — full body persons
   - NO strokes/outlines — pure flat fills
   - Separate head, torso, arms, legs (not blobs)
   - ~4 heads tall, stylized proportions
   - Hair with volume (overlapping shapes)
   - Vibrant skin tones + colorful hair
   - Eyes = small dark dots + white shine
   - Mouth = tiny rounded rectangle
   ═══════════════════════════════════════════════════════════════ */

const TEAL = '#1BC7B1';
const ORANGE = '#FF6A00';
const BLUE = '#6C7AE0';
const PINK = '#E85D75';
const EYE = '#1A1A2E';
const W = '#FFFFFF';
const DK = '#2D2D3A';

/* Skin tones (Kurzgesagt vibrant style) */
const SK1 = '#F4A4B0';   /* warm pink */
const SK2 = '#C8896A';   /* warm brown */

/* Hair colors */
const HR1 = '#5B3D8E';   /* purple */
const HR2 = '#3D2B1F';   /* dark brown */

const scenes = [
    { id: 'order',    step: 1, title: 'Hace su',     accent: 'pedido',   subtitle: 'Elige lo que quiera desde la app', bg: '#E0F5F0', accentColor: '#1BC7B1', duration: 4500 },
    { id: 'kitchen',  step: 2, title: 'Se cocina con', accent: 'amor',   subtitle: 'El chef prepara tu orden al instante', bg: '#FFF0E5', accentColor: '#FF6A00', duration: 6000 },
    { id: 'pickup',   step: 3, title: 'Va en',        accent: 'camino',  subtitle: 'Seguimiento en vivo hasta tu puerta', bg: '#E5EAFF', accentColor: '#6C7AE0', duration: 4500 },
    { id: 'delivery', step: 4, title: 'Llega la',     accent: 'comida',  subtitle: 'Tu pedido caliente en la puerta',     bg: '#FFF8E1', accentColor: '#FF6A00', duration: 6000 },
    { id: 'enjoy',    step: 5, title: '',             accent: 'A comer!', subtitle: 'Disfruta tu comida favorita',         bg: '#E0F8E5', accentColor: '#1BC7B1', duration: 7000 },
];

/* ═══ CUSTOMER — purple hair, teal shirt, holding phone ═══ */
function CustomerPerson({ animate, receiving }) {
    return (
        <svg viewBox="0 0 180 310" className="ol-char">
            <motion.g
                initial={{ y: 20, opacity: 0 }}
                animate={animate ? { y: 0, opacity: 1 } : {}}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            >
                {/* Ground shadow */}
                <ellipse cx="90" cy="296" rx="40" ry="8" fill="rgba(27,199,177,0.14)" />

                {/* ── HAIR BACK (behind head) ── */}
                <ellipse cx="90" cy="44" rx="34" ry="34" fill={HR1} />
                {/* Side locks */}
                <rect x="56" y="40" width="13" height="44" rx="6.5" fill={HR1} />
                <rect x="111" y="40" width="13" height="44" rx="6.5" fill={HR1} />

                {receiving ? (
                    <>
                        {/* ── LEFT ARM (behind body, relaxed) ── */}
                        <rect x="32" y="102" width="24" height="48" rx="12" fill={TEAL} />
                        <rect x="34" y="140" width="20" height="34" rx="10" fill={SK1} />
                        <ellipse cx="44" cy="176" rx="9" ry="8" fill={SK1} />

                        {/* ── BODY ── */}
                        <rect x="50" y="92" width="80" height="92" rx="14" fill={TEAL} />
                        <rect x="94" y="92" width="36" height="92" rx="10" fill="rgba(0,0,0,0.06)" />
                        <path d="M76 92 L90 108 L104 92" fill="#15A89D" />

                        {/* ── RIGHT ARM reaching out to receive ── */}
                        <motion.g
                            animate={animate ? { rotate: [0, 3, 0] } : {}}
                            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                            style={{ transformOrigin: '130px 100px' }}
                        >
                            {/* Upper arm */}
                            <rect x="124" y="96" width="22" height="40" rx="11" fill={TEAL} />
                            {/* Forearm */}
                            <line x1="136" y1="130" x2="152" y2="150" stroke={SK1} strokeWidth="15" strokeLinecap="round" />
                            {/* Hand — palm */}
                            <ellipse cx="156" cy="156" rx="9" ry="8" fill={SK1} />
                            {/* Thumb */}
                            <ellipse cx="149" cy="152" rx="3.5" ry="3" fill={SK1} />
                            {/* Fingers suggestion */}
                            <ellipse cx="162" cy="152" rx="3" ry="5" fill={SK1} />
                        </motion.g>
                    </>
                ) : (
                    <>
                        {/* ── LEFT ARM (behind body) ── */}
                        <rect x="32" y="102" width="24" height="50" rx="12" fill={TEAL} />
                        <rect x="34" y="142" width="20" height="36" rx="10" fill={SK1} />
                        <ellipse cx="44" cy="180" rx="10" ry="9" fill={SK1} />

                        {/* ── BODY (teal shirt) ── */}
                        <rect x="50" y="92" width="80" height="92" rx="14" fill={TEAL} />
                        <rect x="94" y="92" width="36" height="92" rx="10" fill="rgba(0,0,0,0.06)" />
                        <path d="M76 92 L90 108 L104 92" fill="#15A89D" />

                        {/* ── RIGHT ARM + PHONE (animated) ── */}
                        <motion.g
                            animate={animate ? { rotate: [-2, 2, -2] } : {}}
                            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                            style={{ transformOrigin: '134px 110px' }}
                        >
                            <rect x="122" y="102" width="24" height="46" rx="12" fill={TEAL} />
                            <rect x="124" y="138" width="20" height="32" rx="10" fill={SK1} />
                            <ellipse cx="134" cy="172" rx="10" ry="9" fill={SK1} />
                            {/* Phone */}
                            <rect x="124" y="152" width="22" height="36" rx="5" fill={DK} />
                            <rect x="127" y="157" width="16" height="24" rx="3" fill="#4A90D9" />
                            <circle cx="135" cy="166" r="3" fill={ORANGE} />
                        </motion.g>
                    </>
                )}

                {/* ── LEGS ── */}
                <rect x="56" y="178" width="28" height="84" rx="12" fill={BLUE} />
                <rect x="96" y="178" width="28" height="84" rx="12" fill={BLUE} />
                <rect x="96" y="178" width="28" height="84" rx="12" fill="rgba(0,0,0,0.04)" />

                {/* ── SHOES ── */}
                <ellipse cx="70" cy="266" rx="18" ry="9" fill={DK} />
                <ellipse cx="110" cy="266" rx="18" ry="9" fill={DK} />

                {/* ── NECK ── */}
                <rect x="80" y="74" width="20" height="20" rx="4" fill={SK1} />

                {/* ── HEAD ── */}
                <ellipse cx="90" cy="48" rx="26" ry="28" fill={SK1} />

                {/* ── EARS ── */}
                <ellipse cx="64" cy="52" rx="5" ry="7" fill={SK1} />
                <ellipse cx="116" cy="52" rx="5" ry="7" fill={SK1} />

                {/* ── HAIR FRONT ── */}
                <ellipse cx="90" cy="26" rx="30" ry="16" fill={HR1} />
                <ellipse cx="74" cy="32" rx="14" ry="13" fill={HR1} />
                <ellipse cx="106" cy="30" rx="10" ry="11" fill={HR1} />

                {/* ── FACE ── */}
                <circle cx="80" cy="48" r="3.5" fill={EYE} />
                <circle cx="82" cy="46" r="1.2" fill={W} />
                <circle cx="100" cy="48" r="3.5" fill={EYE} />
                <circle cx="102" cy="46" r="1.2" fill={W} />
                {/* Blush */}
                <ellipse cx="72" cy="58" rx="7" ry="3.5" fill="rgba(232,93,117,0.2)" />
                <ellipse cx="108" cy="58" rx="7" ry="3.5" fill="rgba(232,93,117,0.2)" />
                {/* Mouth — bigger smile when receiving */}
                {receiving ? (
                    <>
                        <path d="M80,58 Q90,70 100,58" fill={PINK} />
                        <path d="M83,58 Q90,67 97,58" fill={W} />
                    </>
                ) : (
                    <>
                        <rect x="85" y="60" width="10" height="5" rx="2.5" fill={PINK} />
                        <rect x="87" y="60" width="6" height="2.5" rx="1" fill={W} />
                    </>
                )}
            </motion.g>
        </svg>
    );
}

/* ═══ CHEF — brown skin, white jacket, chef hat, spatula ═══ */
function ChefPerson({ animate }) {
    return (
        <svg viewBox="0 0 320 310" className="ol-char ol-char--wide">
            <motion.g
                initial={{ scale: 0.85, opacity: 0 }}
                animate={animate ? { scale: 1, opacity: 1 } : {}}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            >
                {/* Ground shadow */}
                <ellipse cx="155" cy="298" rx="90" ry="5" fill="rgba(255,106,0,0.06)" />

                {/* ═══ KITCHEN COUNTER / STOVE ═══ */}
                <rect x="140" y="222" width="168" height="68" rx="6" fill="#3A3A4A" />
                <rect x="220" y="222" width="88" height="68" rx="6" fill="rgba(0,0,0,0.04)" />
                <rect x="136" y="214" width="176" height="12" rx="6" fill="#4D4D5C" />
                <rect x="136" y="214" width="176" height="4" rx="2" fill="rgba(255,255,255,0.06)" />
                {/* Knobs */}
                <circle cx="160" cy="252" r="4" fill="#5E5E6E" />
                <circle cx="175" cy="252" r="4" fill="#5E5E6E" />
                <circle cx="160" cy="252" r="2" fill="#777" />
                <circle cx="175" cy="252" r="2" fill="#777" />
                {/* Oven door */}
                <rect x="200" y="238" width="90" height="42" rx="4" fill="rgba(255,255,255,0.04)" />
                <rect x="225" y="244" width="40" height="7" rx="3.5" fill="#5E5E6E" />
                {/* Burner glow */}
                <ellipse cx="200" cy="220" rx="22" ry="3.5" fill="rgba(255,100,0,0.1)" />
                <ellipse cx="200" cy="220" rx="16" ry="2.5" fill="rgba(255,100,0,0.15)" />

                {/* ── Flames (animated) ── */}
                <motion.g
                    animate={animate ? { scaleY: [0.7, 1.2, 0.8, 1.1, 0.7], opacity: [0.6, 1, 0.7, 0.9, 0.6] } : {}}
                    transition={{ duration: 0.6, repeat: Infinity }}
                    style={{ transformOrigin: '200px 218px' }}
                >
                    <ellipse cx="189" cy="216" rx="2.5" ry="5" fill="#FF6A00" opacity="0.7" />
                    <ellipse cx="195" cy="214" rx="3" ry="6" fill="#FF8A00" opacity="0.8" />
                    <ellipse cx="202" cy="215" rx="2.5" ry="5.5" fill="#FFB800" opacity="0.7" />
                    <ellipse cx="208" cy="214" rx="3" ry="6" fill="#FF8A00" opacity="0.8" />
                    <ellipse cx="214" cy="216" rx="2.5" ry="5" fill="#FF6A00" opacity="0.6" />
                </motion.g>

                {/* ═══ PAN WITH FOOD ═══ */}
                <ellipse cx="200" cy="202" rx="30" ry="9" fill="#4A4A5A" />
                <ellipse cx="200" cy="200" rx="30" ry="9" fill="#5A5A6A" />
                <ellipse cx="200" cy="198" rx="26" ry="7" fill="#4A4A54" />
                {/* Handle */}
                <rect x="226" y="196" width="34" height="6" rx="3" fill="#777" />
                <rect x="226" y="196" width="34" height="2.5" rx="1.25" fill="rgba(255,255,255,0.08)" />
                <rect x="254" y="194" width="9" height="10" rx="3" fill="#5E5E6E" />
                {/* Hamburger patty */}
                <ellipse cx="195" cy="195" rx="12" ry="4" fill="#6B3410" />
                <ellipse cx="195" cy="193" rx="12" ry="4" fill="#8B4513" />
                <ellipse cx="195" cy="193" rx="8" ry="2.5" fill="rgba(255,255,255,0.04)" />
                <line x1="188" y1="193" x2="202" y2="193" stroke="rgba(0,0,0,0.1)" strokeWidth="1" />
                {/* Garnish */}
                <ellipse cx="208" cy="195" rx="5" ry="2" fill="#F0D9A0" opacity="0.8" />
                <ellipse cx="184" cy="196" rx="4" ry="1.8" fill="#E74C3C" opacity="0.6" />

                {/* ── Sizzle (animated) ── */}
                <motion.g
                    animate={{ y: [-2, -16, -2], opacity: [0.5, 0, 0.5] }}
                    transition={{ duration: 1.2, repeat: Infinity }}
                >
                    <circle cx="190" cy="184" r="1.5" fill="#FFD700" opacity="0.5" />
                    <circle cx="203" cy="182" r="1" fill="#FFD700" opacity="0.4" />
                    <circle cx="210" cy="185" r="1.2" fill="#FFD700" opacity="0.5" />
                </motion.g>

                {/* ── Steam (animated) ── */}
                <motion.g
                    animate={{ y: [-2, -22, -2], opacity: [0.25, 0, 0.25] }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                >
                    <path d="M190 178 Q187 168 192 158" fill="none" stroke="rgba(180,180,180,0.35)" strokeWidth="2" strokeLinecap="round" />
                    <path d="M200 176 Q203 166 198 154" fill="none" stroke="rgba(180,180,180,0.3)" strokeWidth="2.5" strokeLinecap="round" />
                    <path d="M210 178 Q207 168 212 156" fill="none" stroke="rgba(180,180,180,0.25)" strokeWidth="2" strokeLinecap="round" />
                </motion.g>

                {/* Tomato on counter */}
                <circle cx="148" cy="210" r="6" fill="#E74C3C" />
                <path d="M146 205 Q148 202 150 205" fill="#27AE60" />
                <circle cx="150" cy="209" r="1.5" fill="rgba(255,255,255,0.15)" />
                {/* Cutting board */}
                <rect x="140" y="210" width="20" height="3" rx="1.5" fill="#D4A574" />

                {/* ═══ CHEF ═══ */}

                {/* ── Back leg ── */}
                <line x1="88" y1="200" x2="82" y2="274" stroke="#2A2A38" strokeWidth="15" strokeLinecap="round" />
                <rect x="72" y="272" width="22" height="8" rx="4" fill="#1A1A28" opacity="0.8" />

                {/* ── Front leg ── */}
                <line x1="104" y1="200" x2="110" y2="274" stroke={DK} strokeWidth="17" strokeLinecap="round" />
                <rect x="100" y="272" width="24" height="9" rx="4.5" fill="#1A1A28" />
                <rect x="100" y="279" width="24" height="3" rx="1.5" fill="#111120" />

                {/* ── Torso (white chef jacket) ── */}
                <path d="M76,200 L80,112 Q84,100 98,96 L118,96 Q132,100 130,112 L138,200 Z" fill={W} />
                <path d="M118,96 Q132,100 130,112 L138,200 L120,200 Z" fill="rgba(0,0,0,0.04)" />
                {/* Collar V */}
                <path d="M92,100 L108,116 L122,100" fill="#ECECEC" />
                {/* Buttons */}
                <circle cx="104" cy="132" r="2.5" fill="#D8D8D8" />
                <circle cx="104" cy="150" r="2.5" fill="#D8D8D8" />
                <circle cx="104" cy="168" r="2.5" fill="#D8D8D8" />
                <circle cx="116" cy="132" r="2.5" fill="#D8D8D8" />
                <circle cx="116" cy="150" r="2.5" fill="#D8D8D8" />
                <circle cx="116" cy="168" r="2.5" fill="#D8D8D8" />

                {/* ── Left arm + spatula (animated cooking) ── */}
                <motion.g
                    animate={animate ? { rotate: [0, -6, 0, 4, 0] } : {}}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ transformOrigin: '82px 114px' }}
                >
                    {/* Upper arm (jacket sleeve) */}
                    <line x1="82" y1="114" x2="118" y2="146" stroke={W} strokeWidth="14" strokeLinecap="round" />
                    {/* Forearm (skin) */}
                    <line x1="118" y1="146" x2="155" y2="172" stroke={SK2} strokeWidth="11" strokeLinecap="round" />
                    {/* Wrist */}
                    <circle cx="155" cy="172" r="5.5" fill={SK2} />
                    {/* Hand — gripping spatula */}
                    <g transform="rotate(-20, 160, 180)">
                        {/* Palm */}
                        <ellipse cx="160" cy="178" rx="7" ry="5" fill={SK2} />
                        {/* Fingers wrapping around handle */}
                        <rect x="155" y="181" width="10" height="4" rx="2" fill={SK2} />
                        <rect x="155" y="184" width="9" height="3.5" rx="1.8" fill={SK2} />
                        {/* Thumb on top */}
                        <ellipse cx="163" cy="175" rx="3" ry="2.2" fill={SK2} transform="rotate(-10, 163, 175)" />
                        {/* Spatula handle */}
                        <rect x="157" y="185" width="5" height="24" rx="2.5" fill="#6B6B7B" />
                        <rect x="157" y="185" width="5" height="8" rx="2.5" fill="#7A7A8A" />
                        {/* Spatula head */}
                        <rect x="153" y="207" width="13" height="3" rx="1.5" fill="#8A8A9A" />
                        <path d="M151,210 Q153,207 159.5,207 Q166,207 168,210 L166,216 Q159.5,218 153,216 Z" fill="#A0A0B0" />
                        <path d="M153,216 L166,216 L165,214 L154,214 Z" fill="#8A8A9A" />
                    </g>
                </motion.g>

                {/* ── Right arm (gripping pan handle) ── */}
                {/* Upper arm (jacket sleeve) */}
                <line x1="134" y1="114" x2="188" y2="156" stroke={W} strokeWidth="14" strokeLinecap="round" />
                {/* Forearm (skin) */}
                <line x1="188" y1="156" x2="242" y2="190" stroke={SK2} strokeWidth="11" strokeLinecap="round" />
                {/* Hand gripping pan handle */}
                <g>
                    {/* Palm */}
                    <ellipse cx="248" cy="193" rx="7" ry="5.5" fill={SK2} transform="rotate(-25, 248, 193)" />
                    {/* Fingers wrapping under handle */}
                    <rect x="244" y="196" width="10" height="4" rx="2" fill={SK2} transform="rotate(-25, 249, 198)" />
                    <rect x="244" y="199" width="9" height="3.5" rx="1.8" fill={SK2} transform="rotate(-25, 248, 200)" />
                    {/* Thumb on top of handle */}
                    <ellipse cx="253" cy="190" rx="3.2" ry="2.2" fill={SK2} transform="rotate(-25, 253, 190)" />
                </g>

                {/* ── Neck ── */}
                <line x1="106" y1="96" x2="108" y2="84" stroke={SK2} strokeWidth="12" strokeLinecap="round" />

                {/* ── Head ── */}
                <ellipse cx="108" cy="58" rx="22" ry="26" fill={SK2} />
                <ellipse cx="86" cy="62" rx="5" ry="6.5" fill={SK2} />
                <ellipse cx="86" cy="62" rx="3" ry="4" fill="rgba(0,0,0,0.04)" />
                <ellipse cx="130" cy="62" rx="5" ry="6.5" fill={SK2} />

                {/* ── Chef hat ── */}
                <circle cx="94" cy="22" r="16" fill={W} />
                <circle cx="108" cy="16" r="18" fill={W} />
                <circle cx="122" cy="22" r="16" fill={W} />
                <rect x="84" y="24" width="48" height="18" fill={W} />
                <rect x="82" y="38" width="52" height="7" rx="3.5" fill={ORANGE} />
                <path d="M96 20 Q108 8 120 20" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
                <path d="M116 16 Q126 18 124 22 L124 38 L114 38 Z" fill="rgba(0,0,0,0.03)" />

                {/* ── Face ── */}
                <circle cx="99" cy="56" r="3" fill={EYE} />
                <circle cx="100.5" cy="55" r="1" fill={W} />
                <circle cx="117" cy="56" r="3" fill={EYE} />
                <circle cx="118.5" cy="55" r="1" fill={W} />
                <path d="M95,52 Q99,50 103,52" fill="none" stroke="#4A3A2A" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M113,52 Q117,50 121,52" fill="none" stroke="#4A3A2A" strokeWidth="1.5" strokeLinecap="round" />
                <ellipse cx="108" cy="63" rx="2.5" ry="2" fill="rgba(0,0,0,0.06)" />
                <path d="M101,69 Q108,76 115,69" fill={PINK} />
                <path d="M103,69 Q108,73 113,69" fill={W} />
                <ellipse cx="92" cy="64" rx="5" ry="2.5" fill="rgba(232,93,117,0.12)" />
                <ellipse cx="124" cy="64" rx="5" ry="2.5" fill="rgba(232,93,117,0.12)" />
            </motion.g>
        </svg>
    );
}

/* ═══ RIDER STANDING (delivery scene) — short hair, orange jacket, pro backpack, extending bag ═══ */
function RiderPersonStanding({ animate }) {
    return (
        <svg viewBox="5 0 180 310" className="ol-char">
            <motion.g
                initial={{ y: 20, opacity: 0 }}
                animate={animate ? { y: 0, opacity: 1 } : {}}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            >
                <ellipse cx="90" cy="296" rx="40" ry="8" fill="rgba(108,122,224,0.14)" />

                {/* ── PRO DELIVERY BACKPACK (behind body) ── */}
                {/* Main body — structured, tall */}
                <rect x="12" y="82" width="50" height="64" rx="8" fill={ORANGE} />
                {/* Top flap */}
                <path d="M12,82 Q12,76 18,76 L56,76 Q62,76 62,82 L62,90 L12,90 Z" fill="#D85300" />
                {/* Flap buckle */}
                <rect x="32" y="86" width="10" height="5" rx="2" fill="rgba(255,255,255,0.2)" />
                {/* Side depth shadow */}
                <rect x="42" y="82" width="20" height="64" rx="4" fill="rgba(0,0,0,0.08)" />
                {/* Front panel line */}
                <rect x="16" y="94" width="42" height="1.5" rx="0.75" fill="rgba(255,255,255,0.1)" />
                {/* Zipper line */}
                <line x1="37" y1="90" x2="37" y2="140" stroke="rgba(0,0,0,0.08)" strokeWidth="1.5" />
                {/* Bottom reinforcement */}
                <rect x="12" y="138" width="50" height="8" rx="4" fill="#D85300" />
                {/* Jama logo */}
                <image href="/logo_jama_sinfondo.png" x="18" y="106" width="36" height="22" />
                {/* Strap visible */}
                <path d="M52,82 Q58,78 62,88" fill="none" stroke="#C04A00" strokeWidth="4" strokeLinecap="round" />
                <path d="M52,82 Q56,92 56,100" fill="none" stroke="#C04A00" strokeWidth="4" strokeLinecap="round" />

                {/* ── LEFT ARM (behind body, on strap) ── */}
                <rect x="40" y="102" width="22" height="40" rx="11" fill={ORANGE} />
                <ellipse cx="48" cy="142" rx="8" ry="7" fill={SK2} />

                {/* ── BODY (orange jacket) ── */}
                <rect x="50" y="92" width="80" height="92" rx="14" fill={ORANGE} />
                <rect x="94" y="92" width="36" height="92" rx="10" fill="rgba(0,0,0,0.07)" />
                {/* Zipper */}
                <rect x="88" y="98" width="4" height="80" rx="2" fill="#D85300" />
                {/* Collar */}
                <rect x="66" y="86" width="48" height="10" rx="5" fill="#D85300" />
                {/* Reflective stripe */}
                <rect x="54" y="150" width="72" height="3" rx="1.5" fill="rgba(255,255,255,0.15)" />

                {/* ── LEGS ── */}
                <rect x="56" y="178" width="28" height="84" rx="12" fill={BLUE} />
                <rect x="96" y="178" width="28" height="84" rx="12" fill={BLUE} />
                <rect x="96" y="178" width="28" height="84" rx="12" fill="rgba(0,0,0,0.04)" />

                {/* ── SHOES ── */}
                <ellipse cx="70" cy="266" rx="18" ry="9" fill={DK} />
                <ellipse cx="110" cy="266" rx="18" ry="9" fill={DK} />

                {/* ── RIGHT ARM extending food bag ── */}
                <motion.g
                    animate={animate ? { rotate: [0, -3, 0] } : {}}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ transformOrigin: '130px 100px' }}
                >
                    {/* Upper arm */}
                    <rect x="124" y="96" width="22" height="40" rx="11" fill={ORANGE} />
                    {/* Forearm */}
                    <line x1="136" y1="130" x2="154" y2="152" stroke={SK2} strokeWidth="16" strokeLinecap="round" />
                    {/* Hand — palm + thumb */}
                    <ellipse cx="158" cy="158" rx="9" ry="8" fill={SK2} />
                    <ellipse cx="150" cy="156" rx="4" ry="3" fill={SK2} />
                    {/* Food bag in hand */}
                    <g transform="translate(144, 156)">
                        <rect x="0" y="6" width="38" height="44" rx="8" fill={ORANGE} />
                        <rect x="0" y="6" width="38" height="10" rx="5" fill="#D85300" />
                        <rect x="20" y="6" width="18" height="44" rx="4" fill="rgba(0,0,0,0.06)" />
                        {/* Handle */}
                        <path d="M11,8 Q11,-6 19,-6 Q27,-6 27,8" fill="none" stroke="#C04A00" strokeWidth="3" strokeLinecap="round" />
                        {/* Logo */}
                        <image href="/logo_jama_sinfondo.png" x="5" y="22" width="28" height="16" />
                    </g>
                </motion.g>

                {/* ── NECK ── */}
                <rect x="80" y="74" width="20" height="20" rx="4" fill={SK2} />

                {/* ── HEAD ── */}
                <ellipse cx="90" cy="48" rx="26" ry="28" fill={SK2} />

                {/* ── EARS ── */}
                <ellipse cx="64" cy="52" rx="5" ry="7" fill={SK2} />
                <ellipse cx="116" cy="52" rx="5" ry="7" fill={SK2} />

                {/* ── SHORT HAIR (clean, modern) ── */}
                <ellipse cx="90" cy="28" rx="28" ry="18" fill={HR2} />
                <ellipse cx="90" cy="22" rx="24" ry="14" fill={HR2} />
                {/* Side fade */}
                <rect x="62" y="30" width="8" height="18" rx="4" fill={HR2} />
                <rect x="110" y="30" width="8" height="18" rx="4" fill={HR2} />
                {/* Top volume */}
                <ellipse cx="86" cy="18" rx="18" ry="10" fill={HR2} />
                {/* Subtle highlight */}
                <ellipse cx="82" cy="16" rx="10" ry="5" fill="rgba(255,255,255,0.06)" />

                {/* ── FACE ── */}
                <circle cx="80" cy="50" r="3.5" fill={EYE} />
                <circle cx="82" cy="48" r="1.2" fill={W} />
                <circle cx="100" cy="50" r="3.5" fill={EYE} />
                <circle cx="102" cy="48" r="1.2" fill={W} />
                {/* Eyebrows */}
                <path d="M76,44 Q80,42 84,44" fill="none" stroke="#3D2B1F" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M96,44 Q100,42 104,44" fill="none" stroke="#3D2B1F" strokeWidth="1.5" strokeLinecap="round" />
                {/* Blush */}
                <ellipse cx="72" cy="58" rx="6" ry="3" fill="rgba(232,93,117,0.15)" />
                <ellipse cx="108" cy="58" rx="6" ry="3" fill="rgba(232,93,117,0.15)" />
                {/* Big happy smile */}
                <path d="M82,60 Q90,70 98,60" fill={PINK} />
                <path d="M84,60 Q90,67 96,60" fill={W} />
            </motion.g>
        </svg>
    );
}

/* ═══ RIDER ON SCOOTER (pickup scene) ═══ */
function RiderOnScooter({ animate, moving }) {
    return (
        <svg viewBox="0 -20 350 290" className="ol-char ol-char--wide">
            <motion.g
                initial={{ x: -60, opacity: 0 }}
                animate={animate ? { x: 0, opacity: 1 } : {}}
                transition={{ type: 'spring', stiffness: 150, damping: 20 }}
            >
                <motion.g
                    animate={moving ? { y: [-1.5, 1.5, -1.5] } : {}}
                    transition={{ duration: 0.4, repeat: Infinity }}
                >
                    {/* Ground shadow */}
                    <ellipse cx="160" cy="250" rx="95" ry="6" fill="rgba(27,199,177,0.08)" />

                    {/* ═══ SCOOTER ══════════════════════════════════════════ */}

                    {/* ── Wheels ── */}
                    <circle cx="78" cy="224" r="24" fill="#3D3D4D" />
                    <circle cx="78" cy="224" r="21" fill="#4D4D5C" />
                    <circle cx="78" cy="224" r="14" fill="#5E5E6E" />
                    <circle cx="78" cy="224" r="6" fill="#888" />
                    <circle cx="78" cy="224" r="2.5" fill="#AAA" />
                    <path d="M58 218 Q78 210 98 218" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" />

                    <circle cx="246" cy="224" r="24" fill="#3D3D4D" />
                    <circle cx="246" cy="224" r="21" fill="#4D4D5C" />
                    <circle cx="246" cy="224" r="14" fill="#5E5E6E" />
                    <circle cx="246" cy="224" r="6" fill="#888" />
                    <circle cx="246" cy="224" r="2.5" fill="#AAA" />
                    <path d="M226 218 Q246 210 266 218" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" />

                    {/* ── Fenders ── */}
                    <path d="M54 216 Q54 198 78 198 Q102 198 102 216" fill={TEAL} />
                    <path d="M224 216 Q224 198 246 198 Q268 198 268 216" fill={TEAL} />

                    {/* ── Scooter body ── */}
                    <rect x="96" y="208" width="118" height="10" rx="5" fill={TEAL} />
                    <rect x="156" y="208" width="58" height="10" rx="5" fill="rgba(0,0,0,0.05)" />

                    <path d="M90 212 Q90 174 112 162 L176 162 Q186 162 186 174 L186 212 Z" fill={TEAL} />
                    <path d="M146 212 Q146 174 164 162 L176 162 Q186 162 186 174 L186 212 Z" fill="rgba(0,0,0,0.05)" />

                    <path d="M210 212 L210 172 Q210 150 232 148 L244 148 Q256 148 256 170 L256 212 Z" fill={TEAL} />
                    <path d="M238 212 L238 172 Q238 152 246 148 L244 148 Q256 148 256 170 L256 212 Z" fill="rgba(0,0,0,0.06)" />

                    {/* Seat */}
                    <path d="M120 160 Q120 148 136 148 L182 148 Q192 148 190 160 Z" fill="#3A3A4A" />
                    <path d="M124 154 Q124 146 138 146 L180 146 Q188 146 186 154 Z" fill="#4D4D5C" />
                    <path d="M136 148 Q156 144 178 148" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1" />

                    {/* Handlebar */}
                    <rect x="238" y="130" width="5" height="34" rx="2.5" fill="#777" />
                    <rect x="222" y="124" width="34" height="7" rx="3.5" fill="#5E5E6E" />
                    <rect x="218" y="122" width="9" height="11" rx="4.5" fill="#3A3A48" />
                    <rect x="251" y="122" width="9" height="11" rx="4.5" fill="#3A3A48" />

                    {/* Mirror */}
                    <rect x="254" y="113" width="2" height="10" rx="1" fill="#888" />
                    <ellipse cx="255.5" cy="111" rx="4.5" ry="3" fill="#A8B8CC" />
                    <ellipse cx="256" cy="110" rx="2.5" ry="1.5" fill="rgba(255,255,255,0.2)" />

                    {/* Headlight */}
                    <ellipse cx="253" cy="174" rx="6" ry="8" fill="#FFE882" />
                    <ellipse cx="253" cy="174" rx="3.5" ry="5" fill="#FFFDE0" />

                    {/* Tail light */}
                    <rect x="86" y="198" width="9" height="5" rx="2.5" fill="#FF6B6B" />

                    {/* Exhaust */}
                    <path d="M74 210 Q74 206 78 206 L92 206 Q96 206 96 210 L96 214 Q96 218 92 218 L78 218 Q74 218 74 214 Z" fill="#777" />

                    {/* ── Delivery box ── */}
                    <rect x="88" y="148" width="36" height="3" rx="1.5" fill="#777" />
                    <rect x="74" y="96" width="54" height="54" rx="8" fill={ORANGE} />
                    <rect x="74" y="96" width="54" height="12" rx="6" fill="#D85300" />
                    <rect x="74" y="108" width="27" height="42" rx="4" fill="rgba(255,255,255,0.06)" />
                    <rect x="101" y="96" width="27" height="54" rx="4" fill="rgba(0,0,0,0.06)" />
                    <image href="/logo_jama_sinfondo.png" x="82" y="114" width="36" height="22" />

                    {/* ═══ RIDER (profile, facing right) ═══════════════════ */}

                    {/* ── Back leg (behind, darker for depth) ── */}
                    <line x1="156" y1="154" x2="192" y2="182" stroke="#4A5A88" strokeWidth="15" strokeLinecap="round" />
                    <line x1="192" y1="182" x2="194" y2="204" stroke="#4A5A88" strokeWidth="13" strokeLinecap="round" />
                    <rect x="186" y="202" width="20" height="9" rx="4.5" fill="#2D2D3A" opacity="0.8" />

                    {/* ── Front leg ── */}
                    <line x1="162" y1="154" x2="200" y2="182" stroke={BLUE} strokeWidth="17" strokeLinecap="round" />
                    <line x1="200" y1="182" x2="202" y2="204" stroke={BLUE} strokeWidth="15" strokeLinecap="round" />
                    <rect x="192" y="202" width="24" height="10" rx="5" fill="#2D2D3A" />
                    <rect x="192" y="210" width="24" height="3" rx="1.5" fill="#222230" />

                    {/* ── Torso (orange delivery jacket) ── */}
                    <path d="M150,154 L154,98 Q158,86 170,84 L180,84 Q192,86 190,98 L196,154 Z" fill={ORANGE} />
                    <path d="M180,84 Q192,86 190,98 L196,154 L180,154 Z" fill="rgba(0,0,0,0.06)" />
                    <path d="M160,88 Q168,82 176,82 Q184,82 186,88" fill="#D85300" stroke="#D85300" strokeWidth="1" />
                    <line x1="174" y1="90" x2="174" y2="142" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />

                    {/* ── Back arm (behind body, darker) ── */}
                    <line x1="172" y1="96" x2="200" y2="114" stroke="#D07000" strokeWidth="12" strokeLinecap="round" />
                    <line x1="200" y1="114" x2="220" y2="126" stroke={SK2} strokeWidth="10" strokeLinecap="round" />
                    <circle cx="220" cy="126" r="5" fill={SK2} />

                    {/* ── Front arm ── */}
                    <line x1="178" y1="96" x2="206" y2="116" stroke={ORANGE} strokeWidth="13" strokeLinecap="round" />
                    <line x1="206" y1="116" x2="224" y2="128" stroke={SK1} strokeWidth="11" strokeLinecap="round" />
                    <circle cx="224" cy="128" r="6" fill={SK1} />
                    <path d="M220,126 Q222,122 225,125" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="0.8" />

                    {/* ── Neck ── */}
                    <line x1="172" y1="84" x2="176" y2="74" stroke={SK1} strokeWidth="10" strokeLinecap="round" />

                    {/* ── Head (profile facing right) ── */}
                    <ellipse cx="178" cy="56" rx="16" ry="20" fill={SK1} />
                    <path d="M182,48 Q198,50 198,60 Q198,72 186,74 Q178,74 178,68 Z" fill={SK1} />
                    <ellipse cx="162" cy="58" rx="4" ry="5.5" fill={SK1} />
                    <ellipse cx="162" cy="58" rx="2.5" ry="3.5" fill="rgba(0,0,0,0.04)" />

                    {/* ── Helmet (modern aero profile) ── */}
                    {/* Shell — aerodynamic orange */}
                    <path d="M155,50 Q152,22 170,14 Q182,10 198,14 Q210,20 208,48 Q206,56 196,58 Q178,60 160,58 Q155,56 155,50 Z" fill={ORANGE} />
                    {/* Right side shadow for 3D depth */}
                    <path d="M192,14 Q210,20 208,48 Q206,56 196,58 L194,58 Q204,56 206,48 Q208,22 194,16 Z" fill="rgba(0,0,0,0.1)" />
                    {/* Top highlight — matte reflection */}
                    <path d="M162,22 Q170,14 184,12 Q194,14 200,20" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2.5" strokeLinecap="round" />
                    {/* Dark accent stripe — brand identity */}
                    <path d="M156,44 Q168,38 180,36 Q194,38 206,44" fill="none" stroke="#D85300" strokeWidth="3" strokeLinecap="round" />
                    {/* Ventilation slots */}
                    <rect x="172" y="20" width="8" height="2" rx="1" fill="rgba(255,255,255,0.15)" />
                    <rect x="168" y="24" width="12" height="2" rx="1" fill="rgba(255,255,255,0.1)" />
                    {/* Bottom edge — rubber seal */}
                    <path d="M156,54 Q168,60 180,60 Q192,60 206,54" fill="none" stroke="#D85300" strokeWidth="2.5" strokeLinecap="round" />
                    {/* Visor — large, tinted, semi-transparent */}
                    <path d="M192,44 Q204,38 210,42 L210,54 Q204,60 194,58 Q190,56 190,50 Z" fill="rgba(74,180,220,0.35)" />
                    <path d="M194,44 Q202,40 208,43 L208,48 Q204,46 196,48 Z" fill="rgba(255,255,255,0.25)" />
                    {/* Visor edge frame */}
                    <path d="M192,44 Q204,38 210,42 L210,54 Q204,60 194,58" fill="none" stroke="#D85300" strokeWidth="1.2" />

                    {/* ── Face (profile) ── */}
                    <ellipse cx="190" cy="52" rx="3" ry="3.5" fill={EYE} />
                    <circle cx="191" cy="51" r="1" fill={W} />
                    <path d="M186,48 Q190,46 194,48" fill="none" stroke="#5A4A3A" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M196,56 Q199,58 198,62" fill="none" stroke={SK2} strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M192,66 Q195,68 197,66" fill="none" stroke={PINK} strokeWidth="1.8" strokeLinecap="round" />
                    <ellipse cx="194" cy="62" rx="4" ry="2.5" fill="rgba(232,93,117,0.12)" />

                    {/* ── Speech bubble ── */}
                    <g transform="translate(30, 0)">
                        <path d="M196,4 Q196,-6 208,-6 L294,-6 Q306,-6 306,4 L306,30 Q306,40 294,40 L258,40 L250,52 L253,40 L208,40 Q196,40 196,30 Z" fill={W} />
                        <path d="M196,4 Q196,-6 208,-6 L294,-6 Q306,-6 306,4 L306,30 Q306,40 294,40 L258,40 L250,52 L253,40 L208,40 Q196,40 196,30 Z" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="1" />
                        <text x="251" y="22" textAnchor="middle" fill="#1A1A2E" fontSize="14" fontWeight="800" fontFamily="Sora, Manrope, sans-serif">¡En camino!</text>
                    </g>
                </motion.g>

                {/* Speed lines */}
                {moving && [0, 1, 2, 3].map(i => (
                    <motion.rect key={i}
                        x={6 + i * 3} y={142 + i * 22}
                        width={20 + i * 4} height={3} rx={1.5}
                        fill={TEAL}
                        animate={{ x: [-24, 0, 24], opacity: [0, 0.45, 0] }}
                        transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.12 }}
                    />
                ))}
            </motion.g>
        </svg>
    );
}

/* ═══════════════════════════════════════════════════════════════
   UI PIECES
   ═══════════════════════════════════════════════════════════════ */

function ChatBubble({ text, position = 'top', emoji }) {
    return (
        <motion.div
            className={`ol-bubble ol-bubble--${position}`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.5 }}
        >
            {emoji && <span className="ol-bubble__emoji">{emoji}</span>}
            <span>{text}</span>
        </motion.div>
    );
}

function PhoneMockupClean() {
    return (
        <motion.div className="ol-phone"
            initial={{ scale: 0.85, opacity: 0, rotate: 5 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20, delay: 0.6 }}
        >
            <div className="ol-phone__notch" />
            <div className="ol-phone__header">
                <img src="/logo_jama_sinfondo.png" alt="JAMA" className="ol-phone__logo" />
            </div>
            <div className="ol-phone__restaurant">
                <span>Burger Lab</span>
                <span className="ol-phone__restaurant-rating">&#x2B50; 4.8</span>
            </div>
            <div className="ol-phone__item">
                <div className="ol-phone__food-thumb" style={{ background: '#FFF0E5' }}>
                    <span style={{ fontSize: '18px' }}>&#x1F354;</span>
                </div>
                <div className="ol-phone__info">
                    <div className="ol-phone__name">Hamburguesa XL</div>
                    <div className="ol-phone__price">$5.99</div>
                </div>
            </div>
            <div className="ol-phone__item">
                <div className="ol-phone__food-thumb" style={{ background: '#FFF8E1' }}>
                    <span style={{ fontSize: '18px' }}>&#x1F35F;</span>
                </div>
                <div className="ol-phone__info">
                    <div className="ol-phone__name">Papas fritas</div>
                    <div className="ol-phone__price">$2.99</div>
                </div>
            </div>
            <div className="ol-phone__total">
                <span>Total</span>
                <span>$8.98</span>
            </div>
            <motion.div className="ol-phone__btn"
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                style={{ boxShadow: '0 4px 16px rgba(27,199,177,0.35)' }}
            >
                Pedir ahora
            </motion.div>
        </motion.div>
    );
}

function OrderCardClean() {
    return (
        <motion.div className="ol-ticket"
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20, delay: 0.5 }}
        >
            <div className="ol-ticket__header">&#x1F525; Preparando</div>
            <div className="ol-ticket__num">#0247</div>
            <div className="ol-ticket__items">
                <div className="ol-ticket__row"><span>1x</span> Hamburguesa XL</div>
                <div className="ol-ticket__row"><span>1x</span> Papas fritas</div>
            </div>
            <div className="ol-ticket__divider" />
            <div className="ol-ticket__total">$8.98</div>
            <div className="ol-ticket__timer">
                <span className="ol-ticket__timer-icon">&#x23F1;</span>
                <span>~15 min</span>
            </div>
            <div className="ol-ticket__progress">
                <motion.div className="ol-ticket__progress-fill"
                    initial={{ width: '20%' }}
                    animate={{ width: '65%' }}
                    transition={{ duration: 4, repeat: Infinity, repeatType: 'reverse' }}
                />
            </div>
            <motion.div className="ol-ticket__status"
                animate={{ opacity: [1, 0.7, 1] }}
                transition={{ duration: 1.2, repeat: Infinity }}
            >
                Cocinando...
            </motion.div>
        </motion.div>
    );
}

/* ═══ CITY SKYLINE — Ibarra, Ecuador landmarks ═══ */
function CitySkyline() {
    return (
        <svg viewBox="0 0 960 240" className="ol-city" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
            <defs>
                <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#B8C8D8" stopOpacity="0.04" />
                    <stop offset="100%" stopColor="#8BA0C4" stopOpacity="0.08" />
                </linearGradient>
                <linearGradient id="lakeGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#4A90B8" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#68B0D0" stopOpacity="0.12" />
                </linearGradient>
            </defs>

            {/* ── Sky ── */}
            <rect width="960" height="240" fill="url(#skyGrad)" />


            {/* ══════════════════════════════════════════════════════
                 LAYER 1: VOLCÁN IMBABURA — prominent peaked volcano
                 ══════════════════════════════════════════════════════ */}
            <g opacity="0.14">
                {/* Imbabura main cone — distinctive jagged peak */}
                <path d="M520,200 L600,95 L620,72 L628,65 L636,72 L660,95 L740,200 Z" fill="#7A9A8A" />
                {/* Secondary ridges */}
                <path d="M560,200 L600,120 L620,72 L628,65" fill="none" stroke="#6A8A7A" strokeWidth="0.8" opacity="0.5" />
                <path d="M700,200 L660,120 L636,72 L628,65" fill="none" stroke="#6A8A7A" strokeWidth="0.8" opacity="0.5" />
                {/* Snow/ice on peak */}
                <path d="M612,82 L620,72 L628,65 L636,72 L644,82 L638,78 L632,82 L628,70 L624,82 L618,78 Z" fill="white" opacity="0.3" />

                {/* Huarmi Imbabura (secondary peak) */}
                <path d="M700,200 L740,130 L752,118 L764,130 L800,200 Z" fill="#8AAA98" opacity="0.7" />
                <path d="M742,126 L752,118 L762,126 L756,124 L752,120 L748,124 Z" fill="white" opacity="0.2" />

                {/* Distant range left */}
                <path d="M0,200 Q40,150 100,170 Q160,130 240,165 Q300,145 380,170 Q440,155 520,200 Z" fill="#9AB8A8" opacity="0.5" />
                {/* Distant range right */}
                <path d="M740,200 Q800,155 860,175 Q920,150 960,170 L960,200 Z" fill="#9AB8A8" opacity="0.5" />
            </g>

            {/* ── Foothills / rolling green hills ── */}
            <g opacity="0.08">
                <path d="M0,200 Q60,172 140,185 Q220,168 320,182 Q400,170 500,188 Q600,172 700,185 Q780,175 860,188 Q920,178 960,190 L960,240 L0,240 Z"
                    fill="#6A9A78" />
            </g>

            {/* ══════════════════════════════════════════════════════
                 LAGUNA YAHUARCOCHA — left side
                 ══════════════════════════════════════════════════════ */}
            <g>
                {/* Lake water */}
                <path d="M0,185 Q15,178 35,180 Q55,174 80,178 Q95,175 110,180 L110,200 L0,200 Z" fill="url(#lakeGrad)" />
                {/* Shore line */}
                <path d="M0,185 Q15,178 35,180 Q55,174 80,178 Q95,175 110,180" fill="none" stroke="#5A8AAA" strokeWidth="0.8" opacity="0.25" />
                {/* Water ripples */}
                <path d="M20,188 Q30,186 40,188" fill="none" stroke="#5A8AAA" strokeWidth="0.5" opacity="0.15" />
                <path d="M50,185 Q60,183 70,185" fill="none" stroke="#5A8AAA" strokeWidth="0.5" opacity="0.12" />
                <path d="M30,192 Q45,190 60,192" fill="none" stroke="#5A8AAA" strokeWidth="0.5" opacity="0.1" />
                {/* Shore vegetation */}
                <ellipse cx="108" cy="178" rx="4" ry="3" fill="#5A8A5A" opacity="0.12" />
                <ellipse cx="102" cy="180" rx="3" ry="2.5" fill="#5A8A5A" opacity="0.1" />
            </g>


            {/* ══════════════════════════════════════════════════════
                 CITY BUILDINGS — detailed line-art
                 ══════════════════════════════════════════════════════ */}
            <g opacity="0.32" fill="none" stroke="#3D4F6A" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">

                {/* ── Small houses near lake ── */}
                <rect x="90" y="182" width="16" height="18" rx="1" />
                <path d="M88,182 L98,174 L108,182" />
                <rect x="94" y="186" width="3" height="4" rx="0.5" />
                <rect x="100" y="186" width="3" height="4" rx="0.5" />

                <rect x="110" y="178" width="18" height="22" rx="1" />
                <path d="M108,178 L119,170 L130,178" />
                <rect x="113" y="183" width="4" height="5" rx="0.5" />
                <rect x="121" y="183" width="4" height="5" rx="0.5" />
                <rect x="115" y="192" width="7" height="8" rx="3.5" />

                {/* ═══════ GRAN CÚPULA CON COLUMNATA ═══════ */}
                {/* (Edificio tipo observatorio/gobierno — left-center) */}
                {/* Base with arched colonnade */}
                <rect x="148" y="162" width="72" height="38" rx="1" />
                {/* Columnas */}
                <line x1="155" y1="162" x2="155" y2="200" />
                <line x1="164" y1="162" x2="164" y2="200" />
                <line x1="173" y1="162" x2="173" y2="200" />
                <line x1="182" y1="162" x2="182" y2="200" />
                <line x1="191" y1="162" x2="191" y2="200" />
                <line x1="200" y1="162" x2="200" y2="200" />
                <line x1="209" y1="162" x2="209" y2="200" />
                {/* Arcos entre columnas */}
                <path d="M155,174 Q159.5,168 164,174" />
                <path d="M164,174 Q168.5,168 173,174" />
                <path d="M173,174 Q177.5,168 182,174" />
                <path d="M182,174 Q186.5,168 191,174" />
                <path d="M191,174 Q195.5,168 200,174" />
                <path d="M200,174 Q204.5,168 209,174" />
                {/* Cornisa superior */}
                <line x1="146" y1="162" x2="222" y2="162" />
                <line x1="146" y1="160" x2="222" y2="160" />
                {/* Gran cúpula */}
                <path d="M158,160 Q184,110 210,160" />
                <path d="M164,160 Q184,118 204,160" />
                {/* Costillas de la cúpula */}
                <path d="M172,155 Q184,116 196,155" />
                <line x1="184" y1="112" x2="184" y2="160" />
                {/* Linterna superior */}
                <rect x="179" y="102" width="10" height="10" rx="1" />
                <path d="M179,102 Q184,94 189,102" />
                {/* Ventanitas de la linterna */}
                <line x1="182" y1="102" x2="182" y2="112" />
                <line x1="186" y1="102" x2="186" y2="112" />

                {/* ═══════ CAMPANARIO / BELL TOWER ═══════ */}
                <rect x="232" y="142" width="18" height="58" rx="1" />
                {/* Niveles */}
                <line x1="232" y1="158" x2="250" y2="158" />
                <line x1="232" y1="174" x2="250" y2="174" />
                {/* Ventanas arco */}
                <rect x="236" y="148" width="4" height="6" rx="2" />
                <rect x="244" y="148" width="4" height="6" rx="2" />
                <rect x="236" y="162" width="4" height="6" rx="2" />
                <rect x="244" y="162" width="4" height="6" rx="2" />
                {/* Campanario abierto (top) */}
                <rect x="234" y="128" width="14" height="14" rx="0.5" />
                <rect x="237" y="130" width="3" height="10" rx="1.5" />
                <rect x="242" y="130" width="3" height="10" rx="1.5" />
                {/* Techo cupulado */}
                <path d="M234,128 Q241,120 248,128" />
                {/* Campana */}
                <path d="M238,134 Q241,130 244,134" />

                {/* ── Colonial building (next to tower) ── */}
                <rect x="254" y="168" width="26" height="32" rx="1" />
                <path d="M252,168 L267,158 L282,168" />
                {/* Balcón corrido */}
                <line x1="254" y1="178" x2="280" y2="178" />
                <line x1="256" y1="178" x2="256" y2="181" />
                <line x1="260" y1="178" x2="260" y2="181" />
                <line x1="264" y1="178" x2="264" y2="181" />
                <line x1="268" y1="178" x2="268" y2="181" />
                <line x1="272" y1="178" x2="272" y2="181" />
                <line x1="276" y1="178" x2="276" y2="181" />
                {/* Ventanas arriba */}
                <rect x="258" y="171" width="4" height="4" rx="0.5" />
                <rect x="266" y="171" width="4" height="4" rx="0.5" />
                <rect x="274" y="171" width="4" height="4" rx="0.5" />
                {/* Arcos abajo */}
                <rect x="257" y="186" width="6" height="10" rx="3" />
                <rect x="267" y="186" width="6" height="10" rx="3" />
                <rect x="277" y="186" width="3" height="10" rx="1.5" />

                {/* ── Building with arched facade ── */}
                <rect x="284" y="165" width="30" height="35" rx="1" />
                {/* Double row of arched windows */}
                <rect x="288" y="170" width="5" height="7" rx="2.5" />
                <rect x="296" y="170" width="5" height="7" rx="2.5" />
                <rect x="304" y="170" width="5" height="7" rx="2.5" />
                <rect x="288" y="182" width="5" height="7" rx="2.5" />
                <rect x="296" y="182" width="5" height="7" rx="2.5" />
                <rect x="304" y="182" width="5" height="7" rx="2.5" />
                {/* Cornisa decorada */}
                <line x1="282" y1="165" x2="316" y2="165" />
                <path d="M284,165 Q286,162 288,165 Q290,162 292,165 Q294,162 296,165 Q298,162 300,165 Q302,162 304,165 Q306,162 308,165 Q310,162 312,165 Q314,162 316,165" />

                {/* ═══════ CATEDRAL PRINCIPAL DE IBARRA ═══════ */}
                {/* Main body — wide */}
                <rect x="328" y="152" width="90" height="48" rx="1" />
                {/* Cornisa doble */}
                <line x1="326" y1="152" x2="420" y2="152" />
                <line x1="326" y1="150" x2="420" y2="150" />
                <line x1="328" y1="155" x2="418" y2="155" />

                {/* Torre izquierda */}
                <rect x="328" y="110" width="20" height="42" rx="1" />
                <line x1="328" y1="126" x2="348" y2="126" />
                {/* Campanario abierto */}
                <rect x="330" y="98" width="16" height="12" rx="0.5" />
                <rect x="333" y="100" width="4" height="8" rx="2" />
                <rect x="339" y="100" width="4" height="8" rx="2" />
                {/* Cupulita */}
                <path d="M330,98 Q338,90 346,98" />
                {/* Ventanas torre */}
                <rect x="332" y="114" width="4" height="6" rx="2" />
                <rect x="340" y="114" width="4" height="6" rx="2" />
                <rect x="332" y="130" width="4" height="6" rx="2" />
                <rect x="340" y="130" width="4" height="6" rx="2" />

                {/* Torre derecha */}
                <rect x="398" y="110" width="20" height="42" rx="1" />
                <line x1="398" y1="126" x2="418" y2="126" />
                {/* Campanario abierto */}
                <rect x="400" y="98" width="16" height="12" rx="0.5" />
                <rect x="403" y="100" width="4" height="8" rx="2" />
                <rect x="409" y="100" width="4" height="8" rx="2" />
                {/* Cupulita */}
                <path d="M400,98 Q408,90 416,98" />
                {/* Ventanas torre */}
                <rect x="402" y="114" width="4" height="6" rx="2" />
                <rect x="410" y="114" width="4" height="6" rx="2" />
                <rect x="402" y="130" width="4" height="6" rx="2" />
                <rect x="410" y="130" width="4" height="6" rx="2" />

                {/* Cúpula central grande */}
                <path d="M352,152 Q373,105 394,152" />
                <path d="M358,152 Q373,112 388,152" />
                {/* Costillas */}
                <line x1="373" y1="107" x2="373" y2="152" />
                <path d="M362,148 Q373,110 384,148" />
                {/* Linterna */}
                <rect x="369" y="100" width="8" height="8" rx="1" />
                <path d="M369,100 Q373,92 377,100" />

                {/* Ventanas arco superiores */}
                <rect x="352" y="158" width="6" height="8" rx="3" />
                <rect x="362" y="158" width="6" height="8" rx="3" />
                <rect x="378" y="158" width="6" height="8" rx="3" />
                <rect x="388" y="158" width="6" height="8" rx="3" />
                {/* Ventanas arco inferiores */}
                <rect x="352" y="172" width="6" height="8" rx="3" />
                <rect x="362" y="172" width="6" height="8" rx="3" />
                <rect x="378" y="172" width="6" height="8" rx="3" />
                <rect x="388" y="172" width="6" height="8" rx="3" />
                {/* Gran puerta principal con arco */}
                <rect x="366" y="182" width="14" height="18" rx="7" />
                <line x1="373" y1="182" x2="373" y2="200" />
                {/* Rosetón encima de puerta */}
                <circle cx="373" cy="170" r="5" />
                <circle cx="373" cy="170" r="3" />
                <circle cx="373" cy="170" r="1" />

                {/* ── Edificios coloniales centro-derecha ── */}
                <rect x="424" y="166" width="22" height="34" rx="1" />
                <path d="M422,166 L435,157 L448,166" />
                <rect x="428" y="171" width="4" height="5" rx="0.5" />
                <rect x="436" y="171" width="4" height="5" rx="0.5" />
                <rect x="428" y="182" width="4" height="5" rx="0.5" />
                <rect x="436" y="182" width="4" height="5" rx="0.5" />
                <line x1="424" y1="180" x2="446" y2="180" />
                {/* Balcón */}
                <line x1="426" y1="180" x2="426" y2="183" />
                <line x1="430" y1="180" x2="430" y2="183" />
                <line x1="434" y1="180" x2="434" y2="183" />
                <line x1="438" y1="180" x2="438" y2="183" />
                <line x1="442" y1="180" x2="442" y2="183" />

                <rect x="450" y="170" width="20" height="30" rx="1" />
                <path d="M448,170 L460,162 L472,170" />
                <rect x="453" y="175" width="4" height="5" rx="0.5" />
                <rect x="462" y="175" width="4" height="5" rx="0.5" />
                <rect x="455" y="188" width="8" height="10" rx="4" />

                {/* ── Small church with detailed tower ── */}
                <rect x="478" y="158" width="28" height="42" rx="1" />
                <path d="M476,158 L492,142 L508,158" />
                {/* Campanario */}
                <rect x="487" y="130" width="10" height="12" rx="0.5" />
                <path d="M487,130 Q492,124 497,130" />
                {/* Campana */}
                <path d="M489,134 Q492,130 495,134" />
                {/* Rosetón */}
                <circle cx="492" cy="148" r="4" />
                <circle cx="492" cy="148" r="2" />
                {/* Puerta arco */}
                <rect x="487" y="178" width="10" height="14" rx="5" />
                {/* Ventanas laterales */}
                <rect x="481" y="164" width="4" height="6" rx="2" />
                <rect x="501" y="164" width="4" height="6" rx="2" />

                {/* ── Iglesia con torres (estilo colonial) ── */}
                <rect x="518" y="160" width="36" height="40" rx="1" />
                <line x1="516" y1="160" x2="556" y2="160" />
                {/* Torre izq pequeña */}
                <rect x="518" y="140" width="10" height="20" rx="0.5" />
                <path d="M518,140 L523,134 L528,140" />
                <rect x="520" y="144" width="3" height="5" rx="1.5" />
                <rect x="524" y="144" width="3" height="5" rx="1.5" />
                {/* Torre der pequeña */}
                <rect x="544" y="140" width="10" height="20" rx="0.5" />
                <path d="M544,140 L549,134 L554,140" />
                <rect x="546" y="144" width="3" height="5" rx="1.5" />
                <rect x="550" y="144" width="3" height="5" rx="1.5" />
                {/* Cúpula central */}
                <path d="M528,160 Q536,142 544,160" />
                <line x1="536" y1="148" x2="536" y2="160" />
                {/* Ventanas */}
                <rect x="524" y="166" width="5" height="7" rx="2.5" />
                <rect x="533" y="166" width="5" height="7" rx="2.5" />
                <rect x="542" y="166" width="5" height="7" rx="2.5" />
                {/* Puerta */}
                <rect x="531" y="182" width="10" height="14" rx="5" />

                {/* ── Colonial houses cluster ── */}
                <rect x="560" y="174" width="20" height="26" rx="1" />
                <path d="M558,174 L570,165 L582,174" />
                <rect x="564" y="179" width="4" height="4" rx="0.5" />
                <rect x="572" y="179" width="4" height="4" rx="0.5" />
                <rect x="565" y="190" width="7" height="8" rx="3.5" />

                <rect x="584" y="178" width="18" height="22" rx="1" />
                <path d="M582,178 L593,170 L604,178" />
                <rect x="587" y="183" width="4" height="4" rx="0.5" />
                <rect x="595" y="183" width="4" height="4" rx="0.5" />

                <rect x="606" y="172" width="20" height="28" rx="1" />
                <path d="M604,172 L616,163 L628,172" />
                <rect x="609" y="177" width="4" height="5" rx="0.5" />
                <rect x="618" y="177" width="4" height="5" rx="0.5" />
                {/* Balcón */}
                <line x1="606" y1="184" x2="626" y2="184" />
                <line x1="610" y1="184" x2="610" y2="187" />
                <line x1="614" y1="184" x2="614" y2="187" />
                <line x1="618" y1="184" x2="618" y2="187" />
                <line x1="622" y1="184" x2="622" y2="187" />
                <rect x="610" y="188" width="4" height="5" rx="0.5" />
                <rect x="618" y="188" width="4" height="5" rx="0.5" />

                {/* ═══════ OBELISCO DE IBARRA ═══════ */}
                {/* Aguja principal */}
                <path d="M650,200 L650,145 L657,118 L664,145 L664,200" />
                {/* Escalones base */}
                <rect x="644" y="194" width="26" height="6" rx="1" />
                <rect x="646" y="188" width="22" height="6" rx="1" />
                <rect x="648" y="182" width="18" height="6" rx="1" />
                {/* Relieves decorativos */}
                <line x1="653" y1="155" x2="661" y2="155" />
                <line x1="652" y1="168" x2="662" y2="168" />
                <line x1="651" y1="178" x2="663" y2="178" />
                {/* Estrella en la punta */}
                <path d="M657,118 L659,113 L657,108 L655,113 Z" />
                {/* Medallón */}
                <circle cx="657" cy="162" r="3.5" />

                {/* ═══════ TREN DE LA LIBERTAD ═══════ */}
                {/* Locomotora — right side */}
                {/* Caldera/boiler */}
                <rect x="688" y="183" width="38" height="17" rx="3" />
                {/* Cabina */}
                <rect x="726" y="175" width="16" height="25" rx="1" />
                <rect x="729" y="178" width="9" height="8" rx="0.5" />
                {/* Chimenea */}
                <rect x="694" y="175" width="6" height="8" rx="0.5" />
                <path d="M692,175 L700,175" />
                <rect x="691" y="172" width="10" height="3" rx="1" />
                {/* Ruedas grandes */}
                <circle cx="700" cy="200" r="6" />
                <circle cx="700" cy="200" r="3" />
                <circle cx="715" cy="200" r="6" />
                <circle cx="715" cy="200" r="3" />
                {/* Rueda pequeña trasera */}
                <circle cx="734" cy="200" r="4" />
                <circle cx="734" cy="200" r="2" />
                {/* Biela */}
                <line x1="700" y1="200" x2="715" y2="200" />
                {/* Piloto / cowcatcher */}
                <path d="M688,196 L682,200 L688,200" />
                {/* Ventana cabina */}
                <rect x="730" y="179" width="4" height="5" rx="0.5" />
                <rect x="736" y="179" width="4" height="5" rx="0.5" />
                {/* Dome (steam dome) */}
                <path d="M706,183 Q710,178 714,183" />
                {/* Tubería lateral */}
                <line x1="688" y1="190" x2="726" y2="190" />

                {/* Vagón */}
                <rect x="748" y="185" width="28" height="15" rx="1" />
                <rect x="750" y="187" width="6" height="6" rx="0.5" />
                <rect x="758" y="187" width="6" height="6" rx="0.5" />
                <rect x="766" y="187" width="6" height="6" rx="0.5" />
                <circle cx="756" cy="200" r="4" />
                <circle cx="768" cy="200" r="4" />
                {/* Enganche */}
                <line x1="742" y1="194" x2="748" y2="194" />

                {/* ── Casas coloniales derechas ── */}
                <rect x="790" y="176" width="22" height="24" rx="1" />
                <path d="M788,176 L801,167 L814,176" />
                <rect x="794" y="181" width="4" height="5" rx="0.5" />
                <rect x="803" y="181" width="4" height="5" rx="0.5" />
                <rect x="795" y="191" width="8" height="9" rx="4" />

                <rect x="816" y="172" width="20" height="28" rx="1" />
                <path d="M814,172 L826,163 L838,172" />
                <rect x="820" y="177" width="4" height="5" rx="0.5" />
                <rect x="828" y="177" width="4" height="5" rx="0.5" />
                <line x1="816" y1="184" x2="836" y2="184" />
                <rect x="820" y="186" width="4" height="5" rx="0.5" />
                <rect x="828" y="186" width="4" height="5" rx="0.5" />

                {/* ── Iglesia final ── */}
                <rect x="844" y="168" width="24" height="32" rx="1" />
                <path d="M842,168 L856,160 L870,168" />
                <circle cx="856" cy="174" r="3.5" />
                <circle cx="856" cy="174" r="1.5" />
                <rect x="851" y="184" width="10" height="12" rx="5" />

                {/* ── Torre telecomunicaciones ── */}
                <line x1="888" y1="200" x2="888" y2="165" />
                <line x1="882" y1="190" x2="894" y2="190" />
                <line x1="883" y1="182" x2="893" y2="182" />
                <line x1="884" y1="174" x2="892" y2="174" />
                <line x1="883" y1="182" x2="893" y2="190" />
                <line x1="893" y1="182" x2="883" y2="190" />
                <circle cx="888" cy="163" r="2.5" />

                {/* ── Casas finales ── */}
                <rect x="905" y="180" width="20" height="20" rx="1" />
                <path d="M903,180 L915,172 L927,180" />
                <rect x="909" y="184" width="4" height="5" rx="0.5" />
                <rect x="917" y="184" width="4" height="5" rx="0.5" />

                <rect x="930" y="178" width="18" height="22" rx="1" />
                <path d="M928,178 L939,169 L950,178" />
                <rect x="933" y="183" width="4" height="4" rx="0.5" />
                <rect x="941" y="183" width="4" height="4" rx="0.5" />
            </g>

            {/* ── Ground line ── */}
            <line x1="0" y1="200" x2="960" y2="200" stroke="#3D4F6A" strokeWidth="0.6" opacity="0.15" />

            {/* ══════════════════════════════════════════════════════
                 COLOR ACCENTS — warm highlights
                 ══════════════════════════════════════════════════════ */}
            <g opacity="0.4">
                {/* Techos naranjas/rojos (como en la imagen) */}
                <path d="M88,182 L98,174 L108,182" fill="#C45A3C" opacity="0.2" stroke="none" />
                <path d="M108,178 L119,170 L130,178" fill="#C45A3C" opacity="0.18" stroke="none" />
                <path d="M422,166 L435,157 L448,166" fill="#C45A3C" opacity="0.18" stroke="none" />
                <path d="M448,170 L460,162 L472,170" fill="#C45A3C" opacity="0.15" stroke="none" />
                <path d="M558,174 L570,165 L582,174" fill="#C45A3C" opacity="0.18" stroke="none" />
                <path d="M604,172 L616,163 L628,172" fill="#C45A3C" opacity="0.15" stroke="none" />
                <path d="M788,176 L801,167 L814,176" fill="#C45A3C" opacity="0.18" stroke="none" />
                <path d="M814,172 L826,163 L838,172" fill="#C45A3C" opacity="0.15" stroke="none" />
                <path d="M903,180 L915,172 L927,180" fill="#C45A3C" opacity="0.18" stroke="none" />
                <path d="M928,178 L939,169 L950,178" fill="#C45A3C" opacity="0.15" stroke="none" />

                {/* Cúpula grande */}
                <path d="M164,160 Q184,118 204,160" fill={ORANGE} opacity="0.08" stroke="none" />


                {/* Catedral cúpula */}
                <path d="M358,152 Q373,112 388,152" fill={ORANGE} opacity="0.08" stroke="none" />

                {/* Obelisco estrella */}
                <path d="M657,118 L659,113 L657,108 L655,113 Z" fill={ORANGE} opacity="0.35" stroke="none" />

                {/* TREN — rojo como en la imagen */}
                <rect x="688" y="183" width="38" height="17" rx="3" fill="#CC3333" opacity="0.15" stroke="none" />
                <rect x="726" y="175" width="16" height="25" rx="1" fill="#CC3333" opacity="0.12" stroke="none" />
                <rect x="694" y="175" width="6" height="8" rx="0.5" fill="#1A1A28" opacity="0.12" stroke="none" />

                {/* Ventanas iluminadas (sutiles) */}
                {[[94,186],[121,183],[236,148],[332,114],[402,114],[729,178],[820,177]].map(([cx,cy],i) => (
                    <rect key={`w${i}`} x={cx} y={cy} width="4" height="5" rx="0.5" fill="#FFE8A0" opacity="0.06" stroke="none" />
                ))}

                {/* Campanario bell tower top */}
                <path d="M234,128 Q241,116 248,128" fill={ORANGE} opacity="0.12" stroke="none" />

            </g>


            {/* ── Palm trees ── */}
            <g opacity="0.15" fill="#4A8C6F" stroke="none">
                {/* Palmera 1 — near dome */}
                <rect x="226" y="178" width="2.5" height="16" rx="1" fill="#6B5A3A" />
                <ellipse cx="220" cy="174" rx="7" ry="3" />
                <ellipse cx="234" cy="175" rx="6" ry="2.5" />
                <ellipse cx="227" cy="172" rx="5" ry="3" />

                {/* Palmera 2 — center right */}
                <rect x="635" y="180" width="2.5" height="14" rx="1" fill="#6B5A3A" />
                <ellipse cx="629" cy="176" rx="6" ry="2.5" />
                <ellipse cx="642" cy="177" rx="7" ry="3" />
                <ellipse cx="636" cy="174" rx="5" ry="3" />

                {/* Palmera 3 — right */}
                <rect x="875" y="180" width="2.5" height="14" rx="1" fill="#6B5A3A" />
                <ellipse cx="869" cy="176" rx="6" ry="2.5" />
                <ellipse cx="882" cy="177" rx="7" ry="3" />
            </g>

            {/* ── Trees scattered ── */}
            <g opacity="0.12" fill="#4A8C6F" stroke="none">
                <ellipse cx="140" cy="196" rx="4" ry="7" />
                <ellipse cx="320" cy="197" rx="4" ry="6" />
                <ellipse cx="470" cy="197" rx="4" ry="6" />
                <ellipse cx="555" cy="197" rx="3" ry="5" />
                <ellipse cx="782" cy="196" rx="4" ry="7" />
                <ellipse cx="898" cy="197" rx="4" ry="6" />
            </g>
        </svg>
    );
}

/* ═══════════════════════════════════════════════════════════════
   SCENE CONTENT
   ═══════════════════════════════════════════════════════════════ */

function SceneContent({ sceneId }) {
    switch (sceneId) {
        case 'order':
            return (
                <div className="ol-scene__layout">
                    <div className="ol-scene__character" style={{ transform: 'rotate(-3deg)' }}>
                        <ChatBubble text="Tengo hambre!" position="top" emoji="&#x1F60B;" />
                        <CustomerPerson animate={true} />
                    </div>
                    <PhoneMockupClean />
                </div>
            );
        case 'kitchen':
            return (
                <div className="ol-scene__layout">
                    <div className="ol-scene__character ol-scene__character--wide">
                        <ChatBubble text="&#xA1;Cocinando!" position="top" emoji="&#x1F468;&#x200D;&#x1F373;" />
                        <ChefPerson animate={true} />
                    </div>
                    <OrderCardClean />
                </div>
            );
        case 'pickup':
            return (
                <div className="ol-scene__layout ol-scene__layout--center">
                    <div className="ol-city-wrap">
                        <CitySkyline />
                        <div className="ol-scene__character ol-scene__character--wide">
                            <RiderOnScooter animate={true} moving={true} />
                        </div>
                        <div className="ol-road">
                            <div className="ol-road__pins">
                                <span className="ol-road__pin">&#x1F3EA;</span>
                                <span className="ol-road__pin">&#x1F3E0;</span>
                            </div>
                            <div className="ol-road__surface">
                                <div className="ol-road__lines" />
                            </div>
                        </div>
                    </div>
                </div>
            );
        case 'delivery':
            return (
                <div className="ol-scene__layout ol-scene__layout--delivery">
                    {/* RIDER — left, facing right */}
                    <motion.div className="ol-delivery-person"
                        initial={{ x: -30, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                    >
                        <motion.div className="ol-delivery-person__bubble"
                            initial={{ scale: 0 }} animate={{ scale: 1 }}
                            transition={{ type: 'spring', delay: 0.4 }}
                        >
                            <span className="ol-delivery-person__bubble-emoji">&#x1F389;</span>
                            <span>&#xA1;Ya lleg&#xF3;!</span>
                        </motion.div>
                        <RiderPersonStanding animate={true} />
                    </motion.div>

                    {/* CENTER — notification + sparkles */}
                    <div className="ol-delivery-center">
                        <motion.div className="ol-push"
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ type: 'spring', delay: 0.5 }}
                        >
                            <span className="ol-push__icon">&#x1F4E6;</span>
                            <div className="ol-push__text">
                                <span className="ol-push__title">Jama</span>
                                <span className="ol-push__body">Tu pedido ha llegado</span>
                            </div>
                        </motion.div>
                        <motion.div className="ol-scene__hearts"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1 }}
                        >
                            {['\u2728', '\u2728', '\u2728'].map((h, i) => (
                                <motion.span key={i}
                                    animate={{ y: [-4, -24], opacity: [1, 0], scale: [1, 1.3] }}
                                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.4 }}
                                    className="ol-heart"
                                >{h}</motion.span>
                            ))}
                        </motion.div>
                    </div>

                    {/* CUSTOMER — right, facing left (mirrored) */}
                    <motion.div className="ol-delivery-person"
                        initial={{ x: 30, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                        style={{ transform: 'scaleX(-1)' }}
                    >
                        <motion.div className="ol-delivery-person__bubble"
                            style={{ transform: 'scaleX(-1)' }}
                            initial={{ scale: 0 }} animate={{ scale: 1 }}
                            transition={{ type: 'spring', delay: 0.6 }}
                        >
                            <span className="ol-delivery-person__bubble-emoji">&#x1F60A;</span>
                            <span>&#xA1;Gracias!</span>
                        </motion.div>
                        <CustomerPerson animate={true} receiving={true} />
                    </motion.div>
                </div>
            );
        case 'enjoy':
            return (
                <div className="ol-scene__layout ol-scene__layout--final">
                    <motion.div className="ol-final"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                    >
                        <motion.div className="ol-final__logo"
                            animate={{ scale: [1, 1.02, 1] }}
                            transition={{ duration: 2.5, repeat: Infinity }}
                        >
                            <img src="/logo_jama_unido_color.png" alt="JAMA" className="ol-final__logo-img" />
                        </motion.div>

                        <motion.p className="ol-final__subtitle"
                            initial={{ y: 10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                        >
                            ¡Jama donde todos comen!
                        </motion.p>

                        <div className="ol-final__stars">
                            {[0, 1, 2, 3, 4].map(i => (
                                <motion.span key={i} className="ol-final__star"
                                    initial={{ scale: 0, rotate: -30 }}
                                    animate={{ scale: 1, rotate: 0 }}
                                    transition={{ type: 'spring', delay: 0.3 + i * 0.12 }}
                                >&#x2605;</motion.span>
                            ))}
                        </div>

                        <div className="ol-final__scores">
                            {[
                                { label: 'Velocidad', pct: 92, emoji: '\u26A1' },
                                { label: 'Comida', pct: 98, emoji: '\uD83C\uDF54' },
                                { label: 'Servicio', pct: 96, emoji: '\u2B50' },
                            ].map((row, i) => (
                                <div className="ol-final__score-row" key={row.label}>
                                    <span className="ol-final__emoji">{row.emoji}</span>
                                    <span className="ol-final__label">{row.label}</span>
                                    <div className="ol-final__bar">
                                        <motion.div className="ol-final__bar-fill"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${row.pct}%` }}
                                            transition={{ duration: 0.8, delay: 0.6 + i * 0.15 }}
                                        />
                                    </div>
                                    <motion.span className="ol-final__pct"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 1.0 + i * 0.15 }}
                                    >{row.pct}%</motion.span>
                                </div>
                            ))}
                        </div>

                        <motion.a
                            className="ol-final__ref"
                            href="#features"
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 1.6, type: 'spring' }}
                        >
                            <span className="ol-final__ref-icon">🤑</span>
                            <span className="ol-final__ref-text">
                                <span className="ol-final__ref-label">Gana dinero con cada referido</span>
                                <span className="ol-final__ref-sub">Recibe el <strong>5%</strong> de comisión por cada amigo</span>
                            </span>
                            <span className="ol-final__ref-arrow">→</span>
                        </motion.a>
                    </motion.div>

                    <div className="ol-confetti" aria-hidden="true">
                        {[...Array(12)].map((_, i) => (
                            <motion.div key={i}
                                className="ol-confetti__piece"
                                style={{
                                    left: `${5 + (i * 17) % 90}%`,
                                    width: `${6 + (i % 4) * 3}px`,
                                    height: `${6 + (i % 4) * 3}px`,
                                    borderRadius: i % 3 === 0 ? '2px' : '50%',
                                    background: [ORANGE, TEAL, '#FFE066', '#FF7EB3', '#7EC8E3', '#A78BFA'][i % 6],
                                }}
                                initial={{ y: -10, opacity: 1 }}
                                animate={{
                                    y: ['0vh', '100vh'],
                                    rotate: [0, 360 * (i % 2 ? 1 : -1)],
                                    opacity: [1, 1, 0],
                                }}
                                transition={{
                                    duration: 2.5 + (i % 5) * 0.4,
                                    repeat: Infinity,
                                    delay: (i * 0.18) % 3,
                                    ease: 'linear',
                                }}
                            />
                        ))}
                    </div>
                </div>
            );
        default:
            return null;
    }
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const sceneVariants = {
    enter: { opacity: 0, scale: 0.96 },
    center: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 1.02 },
};

export default function JamaStoryOutline() {
    const [currentScene, setCurrentScene] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);

    const goToScene = useCallback((index) => {
        setCurrentScene(index);
    }, []);

    useEffect(() => {
        if (!isPlaying) return;
        const scene = scenes[currentScene];
        const timer = setTimeout(() => {
            setCurrentScene((prev) => (prev + 1) % scenes.length);
        }, scene.duration);
        return () => clearTimeout(timer);
    }, [currentScene, isPlaying]);

    const scene = scenes[currentScene];

    return (
        <section className="ol-story" style={{
            '--ol-bg': scene.bg,
            '--ol-accent': scene.accentColor,
            '--ol-accent-text': scene.accentColor,
        }}>
            <div className="ol-story__inner">
                <div className="ol-story__header">
                    <motion.div className="ol-story__step-label"
                        key={scene.id}
                        initial={{ y: -10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    >
                        Paso {scene.step} de {scenes.length}
                    </motion.div>
                    <motion.h2 className="ol-story__title"
                        key={`t-${scene.id}`}
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.1 }}
                    >
                        {scene.title && <>{scene.title} </>}<span className="ol-accent-word">{scene.accent}</span>
                    </motion.h2>
                    <motion.p className="ol-story__subtitle"
                        key={`s-${scene.id}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                    >
                        {scene.subtitle}
                    </motion.p>
                </div>

                <div className="ol-story__stage" onClick={() => setIsPlaying(p => !p)}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={scene.id}
                            className="ol-scene"
                            variants={sceneVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                        >
                            <SceneContent sceneId={scene.id} />
                        </motion.div>
                    </AnimatePresence>
                </div>

                <div className="ol-story__progress">
                    {scenes.map((s, i) => (
                        <button
                            key={s.id}
                            className={`ol-story__dot ${i === currentScene ? 'ol-story__dot--active' : ''} ${i < currentScene ? 'ol-story__dot--done' : ''}`}
                            onClick={() => goToScene(i)}
                            aria-label={`Ir a paso ${i + 1}`}
                        />
                    ))}
                </div>

                <button
                    className="ol-story__playpause"
                    onClick={() => setIsPlaying(p => !p)}
                    aria-label={isPlaying ? 'Pausar' : 'Reproducir'}
                >
                    {isPlaying ? '\u23F8' : '\u25B6'}
                </button>
            </div>
        </section>
    );
}
